import React, { useState } from "react";
import {
    CardContent,
    List,
    ListItem,
    CardHeader,
    ListItemText,
    ListItemSecondaryAction,
    Chip,
    IconButton,
    Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import { setSelectedThemes, setPreviewTheme, clearPreview } from "../../Redux/Action";
import { setTheme, setModal } from "../../../../Common/App";
import { SidebarCard } from "../../../../../Components/Cards/SidebarCard";
import { DeleteThemeModal } from "../Modals/DeleteThemeModal";
import { usePermissions } from "../../../../../Hooks/usePermissions";

const StyledList = styled(List)(({ theme }) => ({
    '& .MuiListItem-root': {
        cursor: 'pointer',
        borderRadius: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            transform: 'translateX(4px)',
            '& .MuiListItemSecondaryAction-root .MuiIconButton-root': {
                opacity: 1,
                visibility: 'visible'
            }
        },
        '&.Mui-selected': {
            backgroundColor: theme.palette.primary.light,
            '&:hover': {
                backgroundColor: theme.palette.primary.main,
                transform: 'translateX(4px)',
                '& .MuiListItemSecondaryAction-root .MuiIconButton-root': {
                    opacity: 1,
                    visibility: 'visible'
                }
            }
        },
        '& .MuiListItemSecondaryAction-root .MuiIconButton-root': {
            opacity: 0,
            visibility: 'hidden',
            transition: 'opacity 0.2s ease-in-out',
        }
    }
}));

export const Sidebar = () => {
    const [hoveredId, setHoveredId] = useState(null);
    const dispatch = useDispatch();
    const { hasPermission } = usePermissions();
    const selectedTheme = useSelector((store) => store.themes.selected);
    const items = useSelector((store) => store.themes.list) || [];

    const canDelete = hasPermission("core.themes.delete");

    const handleThemeClick = (item) => {
        dispatch(setSelectedThemes(item.id));
        
        // Get the active theme to restore to
        const activeTheme = items.find(theme => theme.is_active);
        
        // Always clear any existing preview first
        dispatch(clearPreview());
        
        // Always restore to active theme first
        if (activeTheme) {
            dispatch(setTheme(activeTheme.properties || {}));
        }
        
        // If it's not the active theme, show preview
        if (!item.is_active) {
            dispatch(setPreviewTheme(item));
        }
    };

    const handleDeleteClick = (e, item) => {
        e.stopPropagation();
        dispatch(setModal({ component: <DeleteThemeModal theme={item} />, open: true }));
    };

    return (
        <SidebarCard>
            <CardHeader
                title={"Theme Library"}
                subheader={"Select and manage your application themes"}
            />
            <CardContent>
                <StyledList>
                    {items.length === 0 ? (
                        <Typography variant="body1" color="text.secondary" sx={{ px: 2, py: 2 }}>
                            No theme selected
                        </Typography>
                    ) : (
                        items.map((item, index) => (
                            <ListItem
                                key={index}
                                onClick={() => handleThemeClick(item)}
                                onMouseEnter={() => setHoveredId(item.id)}
                                onMouseLeave={() => setHoveredId(null)}
                                sx={{
                                    backgroundColor: selectedTheme?.id === item.id ? "primary.light" : "transparent",
                                    "&.Mui-selected": {
                                        backgroundColor: "primary.light",
                                        "&:hover": {
                                            backgroundColor: "primary.main",
                                        },
                                    },
                                }}
                            >
                                <ListItemText
                                    primary={item.name}
                                    secondary={item?.type === "DARK" ? "Dark Theme" : "Light Theme"}
                                />
                                <ListItemSecondaryAction>
                                    {item?.is_active ? (
                                        <Chip size={"small"} label={"Active"} variant={"outlined"} />
                                    ) : canDelete && hoveredId === item.id ? (
                                        <IconButton
                                            size="small"
                                            aria-label="delete theme"
                                            onClick={(e) => handleDeleteClick(e, item)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    ) : null}
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))
                    )}
                </StyledList>
            </CardContent>
        </SidebarCard>
    );
};
